import { useEffect, useState } from 'react';

import AdminLayout from '../../components/admin/AdminLayout';
import { apiRequest } from '../../services/api';

interface Inquiry {
  id: string;
  kind: 'MATERIALS' | 'CONTACT';
  name: string;
  email: string;
  organization: string | null;
  investorType: string | null;
  message: string | null;
  locale: string;
  adminNotifiedAt: string | null;
  adminNotifyError: string | null;
  confirmationSentAt: string | null;
  confirmationError: string | null;
  createdAt: string;
}

const TYPE_LABEL: Record<string, string> = {
  INDIVIDUAL: 'Individual investor',
  ANGEL: 'Angel investor',
  ADVISER: 'Adviser',
  VC: 'Venture capital',
  FAMILY_OFFICE: 'Family office',
  STRATEGIC: 'Strategic investor',
  OTHER: 'Other',
};

function EmailStatus({ at, error }: { at: string | null; error: string | null }) {
  if (at) return <span className="text-green-700">Sent</span>;
  if (error)
    return (
      <span className="text-red-700" title={error}>
        Failed
      </span>
    );
  return <span className="text-gray-400">Pending</span>;
}

/**
 * /admin/investor-inquiries (PRD §7.2): a read-only list for every admin, newest
 * first, with whether each of the two emails went out.
 */
export default function AdminInvestorInquiries() {
  const [items, setItems] = useState<Inquiry[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiRequest<{ data: { items: Inquiry[]; total: number; pageSize: number } }>(
      `/admin/investor-inquiries?page=${page}`
    )
      .then((res) => {
        setItems(res.data.items);
        setPages(Math.max(1, Math.ceil(res.data.total / res.data.pageSize)));
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <AdminLayout title="Investor inquiries">
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}
      {loading ? (
        <p className="text-gray-600">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-gray-600">No inquiries yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Received</th>
                <th className="px-4 py-3">Form</th>
                <th className="px-4 py-3">From</th>
                <th className="px-4 py-3">Organization</th>
                <th className="px-4 py-3">Message</th>
                <th className="px-4 py-3">Language</th>
                <th className="px-4 py-3">Email to us</th>
                <th className="px-4 py-3">Confirmation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 align-top">
              {items.map((q) => (
                <tr key={q.id}>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                    {new Date(q.createdAt).toLocaleString('en-US')}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {q.kind === 'MATERIALS' ? 'Materials request' : 'Message'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{q.name}</div>
                    <a href={`mailto:${q.email}`} className="text-blue-600 hover:underline">
                      {q.email}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {q.organization}
                    {q.investorType && (
                      <div className="text-xs text-gray-500">
                        {TYPE_LABEL[q.investorType] ?? q.investorType}
                      </div>
                    )}
                  </td>
                  <td className="max-w-[360px] whitespace-pre-wrap px-4 py-3 text-gray-700">
                    {q.message}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{q.locale}</td>
                  <td className="px-4 py-3">
                    <EmailStatus at={q.adminNotifiedAt} error={q.adminNotifyError} />
                  </td>
                  <td className="px-4 py-3">
                    <EmailStatus at={q.confirmationSentAt} error={q.confirmationError} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pages > 1 && (
        <div className="mt-4 flex items-center gap-3 text-sm">
          <button
            type="button"
            className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-40"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => p - 1)}
          >
            Newer
          </button>
          <span className="text-gray-600">
            Page {page} of {pages}
          </span>
          <button
            type="button"
            className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-40"
            disabled={page >= pages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Older
          </button>
        </div>
      )}
    </AdminLayout>
  );
}
